import type { CondicaoPagamento, RegistroBase, RegraComissao, TabelaPreco } from '../db/tipos'
import type { TabelaSync } from '../db/banco'
import { converterNumeroBR } from '../lib/textoBR'
import { regraPagaZeroEmSilencio } from '../comissoes/completude'

// Politica Comercial em RASCUNHO (revisao de 24/09 da spec): condicoes e
// comissoes sao editadas na tela e so gravam no Salvar da aba. Este arquivo
// decide o que gravar -- novo, mudado, removido -- e o que recusar antes.

export interface CondicaoRascunho {
  chave: string
  id: string | null // null = nova, ainda nao gravada
  descricao: string
  desconto: string
  removida: boolean
}

export interface RegraRascunho {
  chave: string
  id: string | null
  tabelaPrecoId: string | null // null = comissao padrao da fornecedora
  tipo: RegraComissao['tipo']
  pctPadrao: string
  gatilho: RegraComissao['gatilho_liberacao']
  removida: boolean
}

export type ErrosDaPolitica = Record<string, string>

export interface GravacaoEmLote {
  tabela: TabelaSync
  registro: Partial<CondicaoPagamento | RegraComissao | TabelaPreco> & { id: string }
}

type Novo = () => Pick<RegistroBase, 'id' | 'criado_em' | 'atualizado_em' | 'apagado_em'>

export const MSG_DESCONTO = 'O desconto deve ser um valor entre 0 e 100 (%).'
export const MSG_CONDICAO_VAZIA = 'Informe a condição (ex.: 30/60/90).'
export const MSG_PCT_FIXO_VAZIO = 'Informe o % da comissão — uma regra "% fixo" sem percentual paga 0% em tudo.'
export const MSG_CONDICAO_PENDENTE = 'Você digitou uma condição e não adicionou. Toque em Adicionar ou apague o texto.'

// Task 16 (escolha dela em 25/09, "1A"): os campos do "adicionar condicao"
// (CondicoesPagamento.tsx) moram aqui, levantados para FormRepresentada, para
// o Salvar da aba poder ver o que esta digitado e ainda nao foi aplicado ao
// rascunho.
export interface CondicaoEmEdicao {
  descricao: string
  desconto: string
  editandoChave: string | null // != null: um "Atualizar" esta aberto neste item
  erroForm: string | null
}

export const CONDICAO_EM_EDICAO_VAZIA: CondicaoEmEdicao = { descricao: '', desconto: '', editandoChave: null, erroForm: null }

// Texto digitado mas nunca aplicado (nem por "Adicionar" nem por
// "Atualizar") nao pode sumir em silencio quando ela toca no Salvar da aba --
// achado dela: digitar, esquecer o botao, tocar Salvar e ver "Salvo" sem a
// condicao aparecer em lugar nenhum. Em branco/so espaco = nada pendente. Com
// um "Atualizar" aberto (editandoChave != null), so conta como pendente se o
// texto DIVERGE do item que esta sendo editado -- reabrir "editar" e nao
// tocar em nada nao pode bloquear o Salvar.
export function condicaoPendente(edicao: CondicaoEmEdicao, itens: CondicaoRascunho[]): boolean {
  const descricao = edicao.descricao.trim()
  const desconto = edicao.desconto.trim()
  if (descricao === '' && desconto === '') return false
  if (edicao.editandoChave === null) return true
  const original = itens.find((c) => c.chave === edicao.editandoChave)
  if (!original) return true
  return original.descricao.trim() !== descricao || original.desconto.trim() !== desconto
}

// ⚠ Com virgula e sem separador de milhar: toString() daria "12.125", que
// converterNumeroBR le como DOZE MIL (ponto + 3 digitos = milhar).
// Exportada (fix round 1 do Task 13): EditorPercentuais.tsx reusa esta MESMA
// funcao para reidratar o campo de "editar" -- dois formatadores discordando
// do mesmo numero e o tipo de bug que so aparece com 3 casas decimais.
export function numeroParaCampo(v: number | null | undefined): string {
  return v == null ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false })
}

export function condicoesEmRascunho(salvas: CondicaoPagamento[]): CondicaoRascunho[] {
  return [...salvas]
    .sort((a, b) => a.ordem - b.ordem)
    .map((c) => ({ chave: c.id, id: c.id, descricao: c.descricao, desconto: numeroParaCampo(c.desconto_pct), removida: false }))
}

function padraoVazio(): RegraRascunho {
  return { chave: 'padrao', id: null, tabelaPrecoId: null, tipo: 'pct_fixo', pctPadrao: '', gatilho: 'faturamento', removida: false }
}

export function regrasEmRascunho(salvas: RegraComissao[]): RegraRascunho[] {
  const lista: RegraRascunho[] = salvas.map((r) => ({
    chave: r.id, id: r.id, tabelaPrecoId: r.tabela_preco_id, tipo: r.tipo,
    pctPadrao: numeroParaCampo(r.pct_padrao), gatilho: r.gatilho_liberacao, removida: false,
  }))
  // A tela sempre mostra o cartao da comissao padrao, exista ela ou nao.
  if (!lista.some((r) => r.tabelaPrecoId === null)) lista.unshift(padraoVazio())
  return lista
}

export function novaRegraDeTabela(tabelaPrecoId: string): RegraRascunho {
  return { chave: `nova-${tabelaPrecoId}`, id: null, tabelaPrecoId, tipo: 'pct_fixo', pctPadrao: '', gatilho: 'faturamento', removida: false }
}

// Ligacoes tabela -> condicao em RASCUNHO (spec 27/09, Parte 2). Chave de
// condicao salva = o id dela; de condicao nova = a chave `nova-cond-...`,
// traduzida para o id gerado so dentro de gravacoesDaPolitica.
export type LigacoesRascunho = Record<string, string[]>

export function ligacoesEmRascunho(tabelas: readonly TabelaPreco[]): LigacoesRascunho {
  return Object.fromEntries(tabelas.map((t) => [t.id, [...(t.condicoes_validas ?? [])]]))
}

export function alternarLigacao(l: LigacoesRascunho, tabelaId: string, chaveCondicao: string): LigacoesRascunho {
  const atual = l[tabelaId] ?? []
  return { ...l, [tabelaId]: atual.includes(chaveCondicao) ? atual.filter((c) => c !== chaveCondicao) : [...atual, chaveCondicao] }
}

// O cartao padrao vazio que ninguem tocou NAO e uma regra: salvar a aba nao
// pode cria-lo nem recusar por "sem %". Sem isto, toda fornecedora sem
// comissao padrao ficaria impedida de salvar a Politica Comercial.
export function padraoIntocado(r: RegraRascunho): boolean {
  return r.id === null && r.tabelaPrecoId === null && r.tipo === 'pct_fixo'
    && r.pctPadrao.trim() === '' && r.gatilho === 'faturamento'
}

export function descontoInvalido(texto: string): boolean {
  if (texto.trim() === '') return false
  const n = converterNumeroBR(texto)
  return n === null || n < 0 || n > 100
}

// Uma regra por tabela fica ESCONDIDA da tela (TabelasEComissao.tsx
// `regraDa`) quando a tabela dela foi apagada -- a regra continua no banco
// (motor.ts ainda recalcula pedido fechado com ela), so nao aparece mais na
// lista. errosDaPolitica usa o MESMO criterio: uma regra que a usuaria nao
// consegue ver nao pode bloquear o Salvar com um erro que ela nao tem como
// corrigir. A regra PADRAO (tabelaPrecoId nulo) nunca fica escondida.
// `tabelasVivasIds` undefined = a lista de tabelas ainda esta carregando --
// nesse caso NAO esconde nada, o mesmo que `visiveis` faz enquanto carrega.
export function regraEscondida(r: RegraRascunho, tabelasVivasIds: ReadonlySet<string> | undefined): boolean {
  return r.tabelaPrecoId !== null && tabelasVivasIds !== undefined && !tabelasVivasIds.has(r.tabelaPrecoId)
}

export function errosDaPolitica(
  condicoes: CondicaoRascunho[],
  regras: RegraRascunho[],
  tabelasVivasIds?: ReadonlySet<string>,
): ErrosDaPolitica {
  const erros: ErrosDaPolitica = {}
  for (const c of condicoes) {
    if (c.removida) continue
    if (c.descricao.trim() === '') erros[c.chave] = MSG_CONDICAO_VAZIA
    else if (descontoInvalido(c.desconto)) erros[c.chave] = MSG_DESCONTO
  }
  for (const r of regras) {
    if (r.removida || padraoIntocado(r) || regraEscondida(r, tabelasVivasIds)) continue
    // Mesma funcao do selo e da faixa (completude.ts): uma definicao so.
    if (regraPagaZeroEmSilencio({ tipo: r.tipo, pct_padrao: converterNumeroBR(r.pctPadrao) } as RegraComissao)) {
      erros[r.chave] = MSG_PCT_FIXO_VAZIO
    }
  }
  return erros
}

export function gravacoesDaPolitica(e: {
  representadaId: string
  condicoesSalvas: CondicaoPagamento[]
  condicoes: CondicaoRascunho[]
  regrasSalvas: RegraComissao[]
  regras: RegraRascunho[]
  novo: Novo
  agora: string
  // F4 (review final): sem isto, uma regra NOVA cuja tabela foi apagada na
  // MESMA edicao (escondida da tela por `regraEscondida`, ver comentario
  // dela) ainda seria INSERTada apontando para uma tabela apagada -- linha
  // que ela nao pode ver nem corrigir.
  tabelasVivasIds?: ReadonlySet<string>
  tabelasSalvas?: TabelaPreco[]
  ligacoes?: LigacoesRascunho
}): GravacaoEmLote[] {
  const saida: GravacaoEmLote[] = []

  const idDaChave = new Map<string, string>()
  let ordem = Math.max(0, ...e.condicoesSalvas.map((c) => c.ordem))
  for (const c of e.condicoes) {
    const salva = c.id === null ? undefined : e.condicoesSalvas.find((s) => s.id === c.id)
    const descricao = c.descricao.trim()
    const desconto_pct = converterNumeroBR(c.desconto)
    if (!salva) {
      // id que sumiu = apagada em outro aparelho durante a edicao: nao ressuscita.
      if (c.id !== null || c.removida) continue
      const registro: CondicaoPagamento = { ...e.novo(), representada_id: e.representadaId, ordem: ++ordem, descricao, desconto_pct }
      idDaChave.set(c.chave, registro.id)
      saida.push({ tabela: 'condicoes_pagamento', registro })
      continue
    }
    if (!c.removida) idDaChave.set(c.chave, salva.id)
    if (c.removida) {
      saida.push({ tabela: 'condicoes_pagamento', registro: { ...salva, apagado_em: e.agora } })
    } else if (salva.descricao !== descricao || (salva.desconto_pct ?? null) !== desconto_pct) {
      // UPDATE no MESMO id (08/09): apagar e recriar perderia o vinculo de pedidos antigos.
      saida.push({ tabela: 'condicoes_pagamento', registro: { ...salva, descricao, desconto_pct } })
    }
  }

  for (const r of e.regras) {
    if (padraoIntocado(r)) continue
    const salva = r.id === null ? undefined : e.regrasSalvas.find((s) => s.id === r.id)
    const pct_padrao = converterNumeroBR(r.pctPadrao)
    if (!salva) {
      if (r.id !== null || r.removida || regraEscondida(r, e.tabelasVivasIds)) continue
      const registro: RegraComissao = {
        ...e.novo(), representada_id: e.representadaId, tabela_preco_id: r.tabelaPrecoId,
        tipo: r.tipo, pct_padrao, gatilho_liberacao: r.gatilho,
      }
      saida.push({ tabela: 'regras_comissao', registro })
      continue
    }
    if (r.removida) {
      saida.push({ tabela: 'regras_comissao', registro: { ...salva, apagado_em: e.agora } })
    } else if (salva.tipo !== r.tipo || salva.pct_padrao !== pct_padrao || salva.gatilho_liberacao !== r.gatilho) {
      saida.push({ tabela: 'regras_comissao', registro: { ...salva, tipo: r.tipo, pct_padrao, gatilho_liberacao: r.gatilho } })
    }
  }

  // So compara o que esta VIVO nos dois lados: id de condicao apagada que
  // ficou na tabela nao conta como mudanca (a leitura ja o ignora --
  // condicoesDaTabela.ts). Sem isto, o Salvar ficaria "pendente" sozinho.
  const vivos = new Set(idDaChave.values())
  for (const t of e.tabelasSalvas ?? []) {
    if (t.apagado_em !== null) continue
    const desejadas = (e.ligacoes?.[t.id] ?? t.condicoes_validas ?? [])
      .map((ch) => idDaChave.get(ch))
      .filter((id): id is string => id !== undefined)
    const atuais = (t.condicoes_validas ?? []).filter((id) => vivos.has(id))
    const mesmo = desejadas.length === atuais.length && [...desejadas].sort().every((id, i) => id === [...atuais].sort()[i])
    if (!mesmo) saida.push({ tabela: 'tabelas_preco', registro: { ...t, condicoes_validas: desejadas } })
  }
  return saida
}
